import { type FC, memo, type PropsWithChildren, useCallback } from 'react';
import { useLabels } from '@/hooks/labels';
import { useNavigation } from '@/hooks/navigation';
import { ImageComponent } from '../Image';
import { Skeleton } from '../Skeleton';

import { ScLabelsSection, ScPromoLabel } from './styled';

export interface PromoLabel {
  character?: string;
  background?: string;
  url?: string;
  onClick?: () => void;
  available?: boolean;
  disableAnimation?: boolean;
  counter?: number | null;
  withBorder?: boolean;
}

interface LabelsSectionProps {
  isDashboard?: boolean;
  onLabelClick?: () => void;
}

export const LabelsSection: FC<PropsWithChildren<LabelsSectionProps>> = ({
  children,
  isDashboard,
  onLabelClick,
}) => {
  const labels = useLabels(isDashboard);

  if (!(labels.length || children)) {
    return null;
  }

  const handleLabelClick = (onClick?: PromoLabel['onClick']) => {
    onClick && onClick();
    onLabelClick && onLabelClick();
  };

  return (
    <ScLabelsSection>
      {labels.map((label) =>
        label.available ? (
          <PromoLabelItem
            {...label}
            key={label.url}
            onClick={() => handleLabelClick(label.onClick)}
          />
        ) : null
      )}
      {children && <ScPromoLabel>{children}</ScPromoLabel>}
    </ScLabelsSection>
  );
};

export const PromoLabelItem: FC<PromoLabel> = memo(
  ({
    character,
    background,
    url,
    disableAnimation,
    onClick,
    counter,
    withBorder,
    available,
  }) => {
    const { navigate } = useNavigation();

    const handleClick = useCallback(() => {
      url && navigate(url);
      onClick && onClick();
    }, [url, onClick, navigate]);

    if (!available) return null;

    if (!background) return <Skeleton.Rect height={48} rounded width={48} />;

    return (
      <ScPromoLabel
        $disableAnimation={disableAnimation}
        $withBorder={withBorder}
        onClick={handleClick}
      >
        <div className="label">
          {character && (
            <ImageComponent
              alt="label"
              className="character"
              fill
              src={character}
            />
          )}
          {counter && <span>{counter}</span>}
        </div>
        <ImageComponent
          alt="bg"
          className="bg"
          height={40}
          src={background}
          width={40}
        />
      </ScPromoLabel>
    );
  }
);
